import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { useT, useTF, currentLocale } from "../i18n";
import type { MatrixClient, Room } from "matrix-js-sdk";

import {
  beginLogin,
  completeLoginIfCallback,
  createSyncedClient,
  logout,
  onSessionLoggedOut,
} from "../auth/oidc";
import { clearHomeserverChoice, config, selectedHomeserverUrl, serverLabel } from "../config";
import { MatrixClientProvider } from "../matrix/clientContext";
import { endOwnSession } from "../matrix/profile";
import { ServerPicker } from "./ServerPicker";
import type { RoomSummary } from "../matrix/rooms";
import {
  acceptInvite,
  createDirectMessage,
  humanError,
  mayCreateSpace,
  mayInviteToSpace,
  mayManageSpaceChild,
  setFavourite,
} from "../matrix/roomActions";
import { useRoom, useRoomLists, useSyncState } from "../matrix/useMatrixRooms";
import { useCallJoin, type CallJoin } from "../matrix/rtc";

/**
 * Сравнение производных списков звонка ПО СОДЕРЖИМОМУ, а не по ссылке.
 * rtc.ts возвращает videoTiles/callParticipants новыми массивами на каждый
 * рендер; по ссылке они «всегда разные», и держатель звонка зацикливал App
 * (100% CPU вкладки при открытой комнате, 2026-09-17). Поля — примитивы
 * и стабильные объекты (MediaStream), поэтому поэлементного сравнения
 * ключей достаточно.
 */
function sameTiles(a: CallJoin["videoTiles"], b: CallJoin["videoTiles"]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i], y = b[i];
    if (
      x.key !== y.key ||
      x.stream !== y.stream ||
      x.local !== y.local ||
      x.label !== y.label ||
      x.participantIdentity !== y.participantIdentity ||
      x.speaking !== y.speaking ||
      x.micMuted !== y.micMuted
    ) {
      return false;
    }
  }
  return true;
}

function sameCallParticipants(
  a: CallJoin["callParticipants"],
  b: CallJoin["callParticipants"],
): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i], y = b[i];
    if (
      x.identity !== y.identity ||
      x.label !== y.label ||
      x.avatarMxc !== y.avatarMxc ||
      x.local !== y.local ||
      x.speaking !== y.speaking ||
      x.micMuted !== y.micMuted
    ) {
      return false;
    }
  }
  return true;
}
import { startNotifier } from "../notifications/notifier";
import { startInviteAutoAccept } from "../notifications/inviteAutoAccept";
import { installSoundUnlock } from "../notifications/sound";
import { askPermissionOnFirstGesture } from "../notifications/askOnLoad";
import { startNativePush, unregisterNativePush } from "../notifications/nativePush";
import { loadNotificationSettings } from "../notifications/settings";
import { deliverSystemNotification } from "../notifications/systemNotification";
import { useUnreadTitle } from "../notifications/useUnreadTitle";
import { useDockBadge } from "../notifications/useDockBadge";
import {
  dismissIncomingCall,
  declineIncomingCall,
  incomingCallTitle,
  useIncomingCall,
  type IncomingCall,
} from "../notifications/useIncomingCall";
import { IncomingCallBanner } from "./IncomingCallBanner";
import { UpdateDialog, canCheckUpdate, checkForUpdate, type AvailableUpdate } from "./UpdateDialog";
import { markChecked, openUpdateUrl, shouldCheckNow, formatUpdateSize } from "./updateCheckUi";
import { mobileCss } from "./mobileCss";
import { ProfileView } from "./ProfileView";
import { fetchOwnProfile } from "../matrix/profile";
import { useAuthedAvatar, useAuthedMedia } from "../matrix/media";
import { RoomDialogs, type DialogRequest } from "./RoomDialogs";
import { RoomList } from "./RoomList";
import { ErrorBoundary } from "./ErrorBoundary";
import { RoomView } from "./RoomView";
import { IconChevronLeft, IconMic, IconMicOff, IconPhoneOff, IconScreenShare, IconVideo, IconVideoOff } from "./icons";
import { useWindowActive } from "./useWindowActive";
import {
  isRoomAlias,
  matrixToFromEvent,
  parseAppLink,
  parseLocationHash,
  publicAppBase,
  roomHash,
} from "./router";
import { RoomLinkGate } from "./RoomLink";
import { palette, s, useNarrowScreen } from "./styles";
import { color, colorOf, radius, size, space } from "./theme";

type Phase = "starting" | "anonymous" | "connecting" | "ready" | "failed";

export function App() {
  const t = useT();
  const tfmt = useTF();
  /**
   * МОБИЛЬНЫЙ РЕЖИМ (спека inbox/mobile-layout/SPEC.md, Антон
   * 2026-09-20 15:21): ≤640px — клиент упакован в Android WebView
   * (Capacitor). ОДИН столбец на весь экран: либо список чатов, либо
   * открытый чат (либо кабинет). Решение «список ИЛИ чат», а не «список
   * рядом»: на 390px две колонки не помещаются, а наложение слоёв
   * ломало бы и жест «назад», и фокус поля ввода.
   *
   * Десктоп не трогаем: вся мобильная вёрстка — условный рендер по
   * `narrow` + CSS внутри @media (max-width:640px) (mobileCss.ts).
   */
  const narrow = useNarrowScreen();
  /**
   * ВЫБОР СЕРВЕРА ИДЁТ ДО ВСЕГО ОСТАЛЬНОГО (Антон, 2026-09-13).
   * Пока сервер не выбран, подключаться некуда: и вход, и сессия принадлежат
   * конкретному homeserver. Состояние здесь, а не в отдельном роутере, чтобы
   * смена сервера из кабинета перерисовывала приложение без перезагрузки.
   */
  const [serverUrl, setServerUrl] = useState<string | null>(() => selectedHomeserverUrl());
  const [phase, setPhase] = useState<Phase>("starting");
  // Состояние разрешения на уведомления. После удаления верхней панели
  // (правка Антона 12.09 12:26 п.3) показывается ТОЛЬКО в личном кабинете
  // (раздел «Уведомления»): шапки, где жила аварийная кнопка, больше нет.
  const [notifPermission, setNotifPermission] = useState<string>(
    typeof Notification === "undefined" ? "unsupported" : Notification.permission,
  );
  const [error, setError] = useState<string | null>(null);
  /**
   * Ошибка ДО редиректа на MAS (динамическая регистрация OIDC, сеть).
   * Постановка Антона/Момо 2026-09-20 19:08: под Capacitor MAS отвергал
   * регистрацию, а пользователь видел «ничего не происходит» — void на
   * beginLogin глотал исключение. Теперь текст ошибки показывается под
   * кнопкой «Войти». Это НЕ phase "failed": до редиректа сессии нет,
   * человек должен иметь возможность нажать «Войти» ещё раз.
   */
  const [loginError, setLoginError] = useState<string | null>(null);
  /**
   * Сессию завершили НА СЕРВЕРЕ (2026-09-26, скриншот Антона).
   * Отдельное состояние, а НЕ phase "anonymous": человек обязан увидеть
   * ПРИЧИНУ, почему его выкинуло, а не молча оказаться на экране входа.
   */
  const [sessionEnded, setSessionEnded] = useState(false);
  const [client, setClient] = useState<MatrixClient | null>(null);
  // Адрес — источник истины про открытую комнату: при загрузке разбираем
  // location.hash, дальше держим его в синхроне (см. navigate и hashchange).
  const [activeRoomId, setActiveRoomId] = useState<string | null>(
    () => parseLocationHash(window.location.hash).roomId,
  );
  /** Событие из адреса (#/room/<id>/<eventId>), к которому надо проскроллить. */
  const [pendingEventId, setPendingEventId] = useState<string | null>(
    () => parseLocationHash(window.location.hash).eventId,
  );
  /**
   * via-серверы из ссылки-приглашения (#/room/<id>?via=…). Нужны только
   * экрану «комната по ссылке» для join; в адрес обратно не пишутся.
   */
  const [routeVia, setRouteVia] = useState<string[] | undefined>(
    () => parseLocationHash(window.location.hash).via,
  );
  /**
   * Комната из адреса оказалась недоступна: не состоим в ней, приглашения
   * нет, либо опечатка в ссылке. Отдельное состояние, а не молчаливый
   * «выберите комнату» — человек, пришедший по чужой ссылке, должен понять,
   * ПОЧЕМУ пусто (гоча из постановки: не белый экран).
   */
  const [unavailableRoomId, setUnavailableRoomId] = useState<string | null>(null);
  // ОДИН слот под диалог: два окна сразу невозможны по типу, а не по дисциплине.
  const [dialog, setDialog] = useState<DialogRequest | null>(null);
  /**
   * ЛИЧНЫЙ КАБИНЕТ (задача 2026-09-12). Отдельный экран, подменяющий
   * колонку комнаты: список чатов остаётся на месте (как у страницы
   * комнаты RoomPage). Вход — свой аватар внизу левой рельсы
   * (OwnRailAvatar, правка Антона 12:26 п.1); состояние здесь, потому
   * что экран рисует App поверх колонки комнаты.
   */
  const [profileOpen, setProfileOpen] = useState(false);
  /**
   * Ошибка действия без диалога (избранное, принятие приглашения).
   * Показываем полосой сверху, а не глотаем: кнопка, которая тихо ничего
   * не сделала, хуже отсутствующей кнопки.
   */
  const [actionError, setActionError] = useState<string | null>(null);
  /**
   * ВЛАДЕЛЕЦ ЗВОНКА — уровень приложения, а НЕ комнаты (критичный баг
   * 2026-09-12, Антон: «сменил комнату — звонок сбрасывается»).
   *
   * Раньше useCallJoin вызывался внутри RoomView, а RoomView умирает при
   * каждой смене комнаты (ErrorBoundary key={roomId}) — и эффект очистки
   * в rtc.ts обрывал звонок. Теперь хук живёт в компоненте
   * ActiveCallHolder, который смонтирован ВНЕ ErrorBoundary и потому
   * переживает любые переходы между комнатами. RoomView получает готовый
   * объект пропом и сам звонок больше не создаёт — двойного хука
   * (и второго членства на то же устройство) нет.
   *
   * callRoomId — комната, которой принадлежит объект звонка прямо сейчас
   * (см. схему привязки в ActiveCallHolder: пока звонка нет, держатель
   * следует за открытой комнатой). callObj — актуальный объект от
   * useCallJoin; держатель кладёт его сюда эффектом после каждого
   * рендера, в котором он изменился.
   *
   * Хуки useState здесь, а не рядом с использованием: выше по функции
   * есть ранние return (starting/failed/anonymous), и любой хук после
   * них нарушил бы порядок вызовов.
   */
  const [callRoomId, setCallRoomId] = useState<string | null>(null);
  const [callObj, setCallObj] = useState<CallJoin | null>(null);
  /**
   * Звонок СВЁРНУТ в мини-фрейм по кнопке «написать» на полном экране
   * (дополнение Антона 2026-09-12 09:20: «чтобы работали титры, надо
   * убедиться, что ты можешь писать в ту же комнату» → вместо второго
   * поля ввода внутри звонка экран сворачивается, открывая обычную
   * ленту и обычный композер). Состояние живёт ЗДЕСЬ, а не в RoomView:
   * мини-фрейм рисует App, и только App знает оба конца переключения.
   * Это РЕЖИМ ПОКАЗА: объект звонка не пересоздаётся, медиа не рвётся.
   */
  const [callMinimized, setCallMinimized] = useState(false);
  /**
   * «Принять» нажали в плашке входящего, но держатель звонка ещё привязан
   * к другой комнате: переход в комнату и перепривязка происходят в этом
   * же клике, а объект звонка приезжает следующим рендером. Запоминаем
   * намерение и выполняем join, как только объект станет нужной комнаты.
   *
   * ПОЧЕМУ ЭТО НЕ ЛОМАЕТ «ТЁПЛЫЙ ЖЕСТ» для микрофона: задержка ровно в
   * один рендер React, внутри той же задачи обработки клика, — браузер
   * по-прежнему считает getUserMedia следствием действия человека.
   * Опасна была бы пауза таймером или ожидание сети.
   */
  const [pendingAcceptRoom, setPendingAcceptRoom] = useState<string | null>(null);
  // «ВАС ДОБАВИЛИ В …»-плашка УДАЛЕНА по требованию Антона (2026-09-27):
  // «отвратное красное сообщение». Само автопринятие приглашений и
  // системное уведомление (Notification API, управляется пользователем)
  // остаются — убран только назойливый баннер поверх интерфейса.
  /**
   * Пространство, выбранное в рельсе слева (null = «Дом»). Нужно ДИАЛОГАМ
   * создания: подсказки людей сужаются до активного пространства
   * (Антон 12.09 10:52). Живёт здесь, потому что RoomList и RoomDialogs —
   * соседи, а не родитель с ребёнком.
   */
  const [activeSpaceId, setActiveSpaceId] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingAcceptRoom) return;
    if (!callObj || callRoomId !== pendingAcceptRoom) return;
    // Звонок мог уже начаться (человек успел нажать «Присоединиться» в
    // комнате) — второй join не нужен.
    if (callObj.state === "idle") callObj.join();
    setPendingAcceptRoom(null);
  }, [pendingAcceptRoom, callObj, callRoomId]);
  // Звонок кончился/упал — свёрнутый режим снимаем, иначе следующий
  // звонок в этой комнате открылся бы сразу мини-фреймом.
  useEffect(() => {
    if (!callObj || callObj.state !== "in-call") setCallMinimized(false);
  }, [callObj]);
  /**
   * Сворачивание по кнопке «написать»: флаг + фокус в обычный композер.
   * Фокус — СНАРУЖИ через DOM по data-testid="composer": Composer.tsx
   * трогать нельзя (граница постановки), а его textarea публично
   * помечена. Фокус откладываем на два кадра: RoomView должен успеть
   * убрать слой CallScreen из-под курсора (без requestAnimationFrame
   * фокус уйдёт в элемент под ещё живым оверлеем и браузер его
   * проигнорирует).
   */
  const minimizeCall = useCallback(() => {
    setCallMinimized(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLTextAreaElement>('[data-testid="composer"]');
        el?.focus();
      });
    });
  }, []);
  /** Приёмник пары (объект, комната) от держателя. Защита от бесконечного
      цикла: объект звонка — НОВЫЙ литерал на каждый рендер держателя,
      поэтому без сравнения по СОДЕРЖИМОМУ setState здесь дёргал бы App на
      каждый его рендер, а рендер App — снова держатель. Сравниваем по
      полям, которые реально читают RoomView и мини-фрейм. */
  const handleCallChange = useCallback((call: CallJoin | null, roomId: string | null) => {
    // Замер 2026-09-17: без содержательного сравнения массивов этот
    // колбэк создавал бесконечный цикл рендера (см. комментарий ниже).

    setCallObj((prev) => {
      if (call === null) return prev === null ? prev : null;
      if (
        prev &&
        prev.state === call.state &&
        prev.error === call.error &&
        prev.participants === call.participants &&
        // videoTiles и callParticipants rtc.ts собирает .map()-ом на каждый
        // рендер — это ВСЕГДА новые массивы, даже пустые. Сравнение по ссылке
        // здесь никогда не проходило, setCallObj получал «новый» объект, App
        // перерендеривался, держатель звонка снова звал onCallChange — и так
        // по кругу без остановки: ~60 полных перерисовок в секунду и 100% CPU
        // вкладки при любой открытой комнате, даже без звонка (Антон,
        // 2026-09-17 11:24, «20% батареи за 20 минут»). Сравниваем по
        // содержимому.
        sameTiles(prev.videoTiles, call.videoTiles) &&
        sameCallParticipants(prev.callParticipants, call.callParticipants) &&
        prev.cameraOn === call.cameraOn &&
        prev.micOn === call.micOn &&
        prev.screenOn === call.screenOn &&
        prev.screenError === call.screenError &&
        // Списки/выбор устройств меняются без смены фазы звонка. Если их
        // не сравнивать, App оставляет прежний CallJoin: меню остаётся
        // пустым после enumerateDevices и не показывает новый активный вход.
        prev.devices === call.devices &&
        prev.activeDevices === call.activeDevices &&
        prev.deviceNotice === call.deviceNotice &&
        prev.diagnosticsAvailable === call.diagnosticsAvailable &&
        prev.soloSecondsLeft === call.soloSecondsLeft &&
        prev.declinedBy === call.declinedBy
      ) {
        return prev;
      }
      return call;
    });
    setCallRoomId((prev) => (prev === roomId ? prev : roomId));
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await completeLoginIfCallback();
        const c = await createSyncedClient();
        if (cancelled) return;
        if (!c) {
          setPhase("anonymous");
          return;
        }
        setClient(c);
        setPhase("connecting");
        // initialSyncLimit небольшой намеренно: на стенде 109 комнат, и
        // тянуть по 20 событий на каждую — это секунды ожидания на пустом
        // экране. Историю открытой комнаты догружаем отдельно (RoomView).
        await c.startClient({ initialSyncLimit: 8 });
        if (cancelled) return;
        setPhase("ready");
      } catch (e) {
        if (cancelled) return;
        setError((e as Error).message);
        setPhase("failed");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const { invites, joined, spaces, totals } = useRoomLists(phase === "ready" ? client : null);
  const syncState = useSyncState(client);
  const activeRoom = useRoom(client, activeRoomId);
  // Активность окна нужна правилу тишины уведомлений: открытая в фокусе
  // комната не пищит и не показывает всплывашки — человек и так смотрит в неё.
  const windowActive = useWindowActive();

  // Refs для notifier'а: обработчик Timeline читает их в момент события
  // (см. startNotifier ниже), а не захватывает устаревшее замыкание.
  const activeRoomIdRef = useRef<string | null>(activeRoomId);
  activeRoomIdRef.current = activeRoomId;
  const windowActiveRef = useRef(windowActive);
  windowActiveRef.current = windowActive;

  /**
   * ВХОДЯЩИЕ ЗВОНКИ (шаг 3, Антон 11.09 12:14: «Ничего не звонит, никаких
   * нотификейшенов нет»). Хук следит за MatrixRTC-сессиями ВСЕХ комнат и
   * сам включает звонок/уведомление ОС; сюда он отдаёт данные для
   * глобального баннера и маркер для заголовка вкладки. Живёт на уровне
   * App, а не комнаты — иначе звонок был бы виден только из открытой
   * комнаты, что и было дефектом.
   *
   * openRoom пишет через ref: navigate здесь ещё не объявлен (TDZ), а хук
   * должен стоять ДО useUnreadTitle — маркер звонка важнее счётчика.
   * navigateRef.current заполняется сразу после объявления navigate ниже,
   * а обработчики (клик по уведомлению ОС) срабатывают только после
   * монтирования — к тому моменту ref уже заполнен.
   */
  const navigateRef = useRef<(roomId: string | null, eventId?: string | null) => void>(() => {});
  const incomingCall = useIncomingCall(phase === "ready" ? client : null, {
    openRoom: (roomId) => navigateRef.current(roomId),
  });
  /**
   * «Скрыть» на баннере: человек попросил не беспокоить про ЭТОТ звонок.
   * Запоминаем roomId и не показываем баннер, пока звонок не кончится
   * (incomingCall станет null) — тогда dismissedCallRoom сбрасывается, и
   * следующий звонок в той же комнате снова прозвонит. Это «сейчас не могу
   * говорить», а не отклонение: протокольного reject в MSC4143 нет.
   */
  const [dismissedCallRoom, setDismissedCallRoom] = useState<string | null>(null);
  useEffect(() => {
    if (!incomingCall) setDismissedCallRoom(null);
  }, [incomingCall]);
  const visibleIncomingCall: IncomingCall | null =
    incomingCall && incomingCall.roomId !== dismissedCallRoom ? incomingCall : null;

  /**
   * Счётчик в заголовке вкладки — из ТОГО ЖЕ агрегата, что кормит рельс и
   * фильтры (totals из buildRoomLists): «(3)» в заголовке и цифры на
   * иконках обязаны совпадать (требование Антона 2026-09-10 13:34), а два
   * независимых подсчёта разошлись бы на первом же пограничном случае.
   * По модели 13:49 внутри — число ЖДУЩИХ ЧАТОВ (notifying > 0) ПЛЮС
   * приглашения (каждое = 1): «меня ждут в 3 чатах и 1 приглашении».
   *
   * Override звонка: пока идёт ВХОДЯЩИЙ звонок, заголовок показывает
   * «📞 Идёт звонок · <комната>» вместо счётчика — звонок важнее цифры.
   */
  useUnreadTitle(
    totals.allUnread,
    incomingCall ? incomingCallTitle(incomingCall) : null,
  );
  // Бейдж в Доке Mac — то же число, что в заголовке (звонок его не перекрывает).
  useDockBadge(totals.allUnread);

  /**
   * АВТОПРИНЯТИЕ ПРИГЛАШЕНИЙ (задача 2026-09-12): любое НОВОЕ приглашение
   * текущей сессии принимается само, без кнопок. Механизм и отсечка старых
   * — в notifications/inviteAutoAccept.ts (комментарий-шапка). Здесь только
   * подписка и два приёмника: успех → системное уведомление (опционально,
   * управляется настройкой), ошибка → полоса. Баннер «Вас добавили в …»
   * удалён (Антон, 2026-09-27). Список приглашений в RoomList не трогаем:
   * после join комната уезжает из invites сама, секция просто пустеет.
   */
  useEffect(() => {
    if (phase !== "ready" || !client) return;
    return startInviteAutoAccept(client, {
      onAutoJoined: (info) => {
        const notificationSettings = loadNotificationSettings();
        if (notificationSettings.enabled && notificationSettings.categories.invites) {
          const inviteBody = info.inviterLabel
            ? `${tfmt("app.inviteNotice", { room: info.roomName })} ${tfmt("app.invitedBy", { user: info.inviterLabel })}`
            : tfmt("app.inviteNotice", { room: info.roomName });
          void deliverSystemNotification("alg(success)", {
            body: inviteBody,
            tag: `alg-invite-${info.roomId}`,
            renotify: true,
            silent: false,
            data: {
              url: `${window.location.origin}${window.location.pathname}#${roomHash(info.roomId)}`,
            },
          }).catch((error) => console.warn("[notifications] invite delivery failed:", error));
        }
      },
      onError: (message) => setActionError(message),
    });
  }, [phase, client]);

  /**
   * ПРИНУДИТЕЛЬНЫЙ РАЗЛОГИН: сервер отверг токены (MAS kill-sessions).
   *
   * До 26.09 этого обработчика НЕ БЫЛО ВООБЩЕ: выкинутый клиент продолжал
   * показывать комнаты из кеша с красной строкой «Token is not active».
   *
   * Подписка по client, А НЕ по phase === "ready": 401 может прилететь уже во
   * время startClient(), то есть в фазе "connecting" — иначе ровно тот случай,
   * когда сессию убили при закрытом приложении, остался бы непойманным.
   *
   * ПОРЯДОК ШАГОВ — как в ручном логауте (onLogout ниже), но с двумя
   * отличиями, и оба вынужденные:
   *  1) unregisterNativePush — best-effort. removePusher требует ЖИВОГО токена
   *     (nativePush.ts:440), а здесь он уже мёртв — серверная часть скорее
   *     всего не отработает. Вызываем всё равно: внутри есть
   *     plugin.unregister(), который гасит токен Firebase на САМОМ устройстве
   *     и работает без сети. Блокировать выход он не имеет права.
   *  2) endOwnSession НЕ ВЫЗЫВАЕМ вообще. Сессия уже завершена сервером —
   *     это и есть причина события; запрос с мёртвым токеном гарантированно упадёт
   *     и в ручном пути бросает наружу, т.е. запер бы человека в мёртвом UI.
   *
   * stopClient() ОБЯЗАТЕЛЕН до logout(): иначе цикл sync продолжит стучаться
   * мёртвым токеном и сыпать 401 в консоль уже после выхода.
   *
   * ПЕРЕЗАГРУЗКИ НЕТ (в отличие от ручного логаута): location.reload() увезёт
   * экран с объяснением раньше, чем человек его прочтёт, и получится ровно
   * то «молча мигнуть на логин», которое запрещено постановкой.
   */
  useEffect(() => {
    if (!client) return;
    return onSessionLoggedOut(client, () => {
      void (async () => {
        try {
          await unregisterNativePush(client);
        } catch {
          /* пушер не снялся: токен мёртв, это ожидаемо и не повод
             держать человека в аккаунте, в который он уже не входит. */
        }
        try {
          client.stopClient();
        } catch {
          /* клиент мог быть ещё не запущен (401 во время startClient). */
        }
        logout();
        // Сброс выбора сервера — тем же путём, что и «сменить сервер»
        // (clearHomeserverChoice): после выхода первый экран — выбор
        // сервера, а не форма логина с зашитым адресом (Антон, 05.10).
        // setServerUrl(null) обязателен: reload здесь НЕТ, без setState
        // ранний return `if (!serverUrl)` не сработает и останется логин.
        clearHomeserverChoice();
        setServerUrl(null);
        setClient(null);
        setSessionEnded(true);
        setPhase("anonymous");
      })();
    });
  }, [client]);

  /** Обёртка для действий без своего окна: одно место показа ошибки. */
  const runAction = async (fn: () => Promise<void>) => {
    setActionError(null);
    try {
      await fn();
    } catch (e) {
      setActionError(humanError(e));
    }
  };

  /**
   * Навигация — единственная точка, где адрес МЕНЯЕТСЯ из интерфейса.
   *
   * Пишем в адрес ТОЛЬКО когда он реально отличается: иначе получим цикл
   * «hashchange → setState → снова пишем hash → hashchange» (гоча из
   * постановки). pushState, а не location.hash = …: второй всегда добавляет
   * запись в историю и заодно генерирует hashchange, который нам здесь
   * не нужен — состояние мы уже выставили сами.
   */
  const navigate = useCallback((roomId: string | null, eventId: string | null = null) => {
    const nextHash = roomId ? `#${roomHash(roomId, eventId ?? undefined)}` : "";
    if (window.location.hash !== nextHash) {
      const url = nextHash
        ? `${window.location.pathname}${window.location.search}${nextHash}`
        : `${window.location.pathname}${window.location.search}`;
      // НАЗАД ИЗ ЧАТА → СПИСОК (спека мобильной раскладки 2026-09-20,
      // этап 2). Когда комнату ЗАКРЫВАЕМ (roomId=null) и текущая запись
      // истории — комната, её открытие было отдельным шагом истории
      // (открытие делает pushState ниже), значит «назад» = шаг
      // history.back(), и в списке остаётся единственная запись: второй
      // «назад» из списка выходит из приложения (Android), как в WhatsApp.
      // Пришли по прямой ссылке (запись одна, комнату не открывали из
      // списка) — back() ушёл бы на внешний referrer или в никуда; поэтому
      // страхуемся: если через короткий тик hash не сменился, делаем
      // replaceState. Состояние после back() обновит popstate/hashchange-
      // обработчик ниже — дублировать setState здесь не нужно.
      const closingRoom = roomId === null && window.location.hash.startsWith("#/room/");
      if (closingRoom) {
        window.history.back();
        window.setTimeout(() => {
          if (window.location.hash.startsWith("#/room/")) {
            window.history.replaceState(null, "", url);
          }
        }, 250);
        return;
      }
      window.history.pushState(null, "", url);
    }
    setActiveRoomId(roomId);
    setPendingEventId(eventId);
    setRouteVia(undefined);
    setUnavailableRoomId(null);
  }, []);
  // navigateRef для useIncomingCall (хук стоит выше по файлу и не может
  // захватить navigate напрямую из-за TDZ). Заполняем синхронно при
  // рендере — обработчики звонка сработают только после монтирования.
  navigateRef.current = navigate;

  /**
   * Браузерные уведомления о входящих. Подписка живёт, пока жив клиент;
   * контекст отдаём геттерами, а не значениями: обработчик Timeline должен
   * видеть АКТУАЛЬНЫЕ открытую комнату и фокус в момент события, а не те,
   * что были на рендере подписки (иначе после перехода в другую комнату
   * уведомления о ней продолжили бы всплывать).
   */
  useEffect(() => {
    if (phase !== "ready" || !client) return;
    // Звук браузер разрешает только после жеста человека — вешаем
    // разблокировку один раз (см. sound.ts, проверка Антона 14:42).
    installSoundUnlock();
    // Разрешение спрашиваем САМИ при заходе (постановка Антона 14:49) —
    // на первом жесте человека, иначе Safari проглотит запрос молча.
    // Ответ браузера запоминаем: кабинет покажет его в разделе
    // «Уведомления» (после удаления шапки это единственное место вывода).
    askPermissionOnFirstGesture((p) => {
      setNotifPermission(p);
    });
    return startNotifier(client, {
      getActiveRoomId: () => activeRoomIdRef.current,
      isWindowActive: () => windowActiveRef.current,
      openRoom: (roomId) => navigate(roomId),
    });
    // navigate стабилен (useCallback []). Пересоздавать подписку на смену
    // комнаты нельзя: событие, пришедшее в щель между отпиской и подпиской,
    // было бы потеряно.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, client, navigate]);

  /**
   * ПУШ В НАТИВНОЙ ОБОЛОЧКЕ (Android/Capacitor).
   *
   * Отдельным эффектом, а не внутри блока браузерных уведомлений выше:
   * это ДРУГОЙ канал (FCM + pusher на homeserver'е против Notification API
   * в живой вкладке), и сбой одного не должен уносить другой.
   *
   * В браузере startNativePush — синхронный no-op: ни разрешений, ни сети,
   * ни логов. Обычный Web Push (push.ts + push-gateway) не затрагивается.
   */
  useEffect(() => {
    if (phase !== "ready" || !client) return;
    return startNativePush(client);
  }, [phase, client]);

  /**
   * ПРОВЕРКА ОБНОВЛЕНИЯ ANDROID-ПРИЛОЖЕНИЯ (задача Антона 2026-10-05 17:09).
   *
   * До этой правки проверки обновлений в приложении НЕ БЫЛО вообще:
   * серверная половина (latest.json) живая, клиентской — ни строки.
   * Механика — в src/ui/UpdateDialog.tsx (комментарий-шапка): мост в
   * Capacitor App-плагин тем же способом, что nativeShare.ts, fetch
   * манифеста, ЧИСЛОВОЕ сравнение versionCode. Здесь только жизненный
   * цикл: проверка при СТАРТЕ (phase → ready) и при ВОЗВРАТЕ ИЗ ФОНА
   * (visibilitychange → visible), но не чаще раза в 30 минут
   * (shouldCheckNow/markChecked — отметка в localStorage переживает
   * перезапуск).
   *
   * В браузере и на десктопе canCheckUpdate() = false → ни сети, ни
   * слушателей, ни одной строки нативного кода. Любой сбой внутри
   * checkForUpdate возвращает null молча: фоновая проверка не имеет права
   * ронять приложение или показывать свои ошибки.
   */
  const [update, setUpdate] = useState<AvailableUpdate | null>(null);
  useEffect(() => {
    if (phase !== "ready") return;
    if (!canCheckUpdate()) return;
    let cancelled = false;
    const run = () => {
      // Отметка о попытке ставится ДО запроса: иначе при отсутствии сети
      // мы стучались бы на каждый возврат из фона.
      if (!shouldCheckNow()) return;
      markChecked();
      void checkForUpdate().then((found) => {
        if (!cancelled && found) setUpdate(found);
      });
    };
    run();
    const onVisible = () => {
      if (document.visibilityState === "visible") run();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [phase]);

  /**
   * Обратная сторона: кнопки «назад»/«вперёд» браузера и переход по
   * вставленной ссылке. popstate — для pushState-истории, hashchange —
   * для ручной правки адреса и ссылок с других вкладок. Здесь адрес
   * ТОЛЬКО читаем — писать его обратно нельзя (тот же цикл).
   */
  useEffect(() => {
    const onNav = () => {
      const { roomId, eventId, via } = parseLocationHash(window.location.hash);
      setActiveRoomId(roomId);
      setPendingEventId(eventId);
      setRouteVia(via);
      setUnavailableRoomId(null);
    };
    window.addEventListener("hashchange", onNav);
    window.addEventListener("popstate", onNav);
    return () => {
      window.removeEventListener("hashchange", onNav);
      window.removeEventListener("popstate", onNav);
    };
  }, []);

  /**
   * АЛИАС В АДРЕСЕ (ссылка-приглашение «#/room/%23name:server», Антон
   * 16.10). Остальной клиент работает с room_id, поэтому алиас здесь же
   * превращаем в id: сначала по локальным комнатам (canonical/alt alias —
   * без сети), затем через каталог сервера. Если мы в комнате (или
   * приглашены) — подменяем адрес на id через replaceState (запись истории
   * не плодим). Если нет — алиас остаётся, и эффект ниже покажет экран
   * «комната по ссылке» (вход или «закрытая, попросите приглашение»).
   */
  useEffect(() => {
    if (phase !== "ready" || !client || !activeRoomId || !isRoomAlias(activeRoomId)) return;
    if (syncState !== "SYNCING" && syncState !== "PREPARED") return;
    const alias = activeRoomId;
    let cancelled = false;
    const switchTo = (id: string) => {
      if (cancelled) return;
      const m = client.getRoom(id)?.getMyMembership();
      if (m !== "join" && m !== "invite") return;
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}#${roomHash(id, pendingEventId ?? undefined)}`,
      );
      setActiveRoomId(id);
      setRouteVia(undefined);
      setUnavailableRoomId(null);
    };
    const local = client
      .getRooms()
      .find((r) => r.getCanonicalAlias() === alias || r.getAltAliases().includes(alias));
    if (local) {
      switchTo(local.roomId);
      return;
    }
    client
      .getRoomIdForAlias(alias)
      .then((res) => switchTo(res.room_id))
      .catch(() => {
        /* Неизвестный алиас — экран «по ссылке» скажет «не найдена». */
      });
    return () => {
      cancelled = true;
    };
  }, [phase, client, activeRoomId, syncState, pendingEventId]);

  /**
   * Комната из адреса недоступна: клиент её не знает (не состоим, не
   * приглашены, опечатка в ссылке). Ждать sync обязательно: до PREPARED
   * getRoom честно отвечает null про ЛЮБУЮ комнату, и гнать человека в
   * «недоступно» по сырому sync было бы ложью. Поэтому проверяем только
   * когда список комнат уже построен (syncState SYNCING/PREPARED), и даём
   * приглашениям проход: invite-комнату открыть можно (плашка приглашения).
   */
  useEffect(() => {
    if (phase !== "ready" || !client || !activeRoomId) return;
    if (syncState !== "SYNCING" && syncState !== "PREPARED") return;
    // Даём sync один тик на догрузку: initial sync приходит пачками, и
    // комната может появиться событием чуть позже первого SYNCING. Таймаут
    // 3 секунды — компромисс между «показать недоступность сразу» и «не
    // соврать на медленном sync». После него — честное состояние.
    // leave/ban тоже «недоступно»: комната известна, но лента мёртвая.
    const usable = () => {
      const r = client.getRoom(activeRoomId);
      const m = r?.getMyMembership();
      return m === "join" || m === "invite";
    };
    if (usable()) {
      setUnavailableRoomId(null);
      return;
    }
    const id = setTimeout(() => {
      if (!usable()) setUnavailableRoomId(activeRoomId);
    }, 3000);
    return () => clearTimeout(id);
    // activeRoom в зависимостях: если комната доехала поздним sync уже после
    // показа «недоступна», эффект перезапустится и снимет его (ветка usable).
  }, [phase, client, activeRoomId, activeRoom, syncState]);

  /**
   * Перехват кликов по matrix.to-ссылкам ВНУТРИ клиента (патч Антона
   * 2026-09-10 10:31: «все должно открываться в нашем клиенте а не в
   * элементе»). Вешаем на document в capture-фазе: ссылки могут появиться
   * в любом компоненте (пилюли упоминаний в formatted_body, цитаты
   * реплаев), и держать по обработчику на каждый — верный способ однажды
   * забыть один. Внешние ссылки (не matrix.to) не трогаем вообще.
   */
  useEffect(() => {
    if (!client) return;
    const onClick = (e: MouseEvent) => {
      // Только обычный клик левой кнопкой без модификаторов: Ctrl/Cmd+клик
      // и средняя кнопка — это «открыть в новой вкладке», и отбирать у
      // человека эту возможность мы не вправе.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      // Ссылка на НАШ клиент (ссылка-приглашение или permalink, вставленные
      // в чат) — открываем здесь же, без перезагрузки вкладки.
      const a = (e.target as HTMLElement | null)?.closest?.("a[href]");
      const appTarget = a
        ? parseAppLink(a.getAttribute("href") ?? "", [
            `${window.location.origin}${window.location.pathname}`,
            publicAppBase(),
          ])
        : null;
      if (appTarget?.roomId) {
        e.preventDefault();
        navigate(appTarget.roomId, appTarget.eventId);
        if (appTarget.via) setRouteVia(appTarget.via);
        return;
      }
      const target = matrixToFromEvent(e);
      if (!target) return;
      e.preventDefault();

      if (target.kind === "user") {
        // Упоминание человека → личный чат с ним. Сначала ищем СУЩЕСТВУЮЩИЙ
        // DM в m.direct: createDirectMessage без этого плодил бы дубли
        // комнат при каждом клике на пилюлю. Отдельного экрана «профиль»
        // в клиенте нет — DM и есть то, что показываем.
        void runAction(async () => {
          // Поиск существующего DM живёт внутри createDirectMessage (запрет
          // дублей, Антон 14.09): там он видит и комнаты, созданные собеседником,
          // которых нет в нашем m.direct. Дублировать проверку здесь значило бы
          // держать две разные логики одного правила.
          navigate(await createDirectMessage(client, target.userId));
        });
        return;
      }
      if (target.kind === "room") {
        // Комната известна локально — просто открываем (с переходом к
        // событию, если оно в ссылке). Неизвестная — попадёт в ветку
        // «недоступна» по эффекту выше: это и есть честное состояние для
        // комнаты, в которой мы не состоим.
        navigate(target.roomId, target.eventId);
        return;
      }
      // Алиас (#room:server): открываем как адрес — разрешение алиаса и
      // экран «комната по ссылке» для не-участника живут в эффектах выше
      // (один путь и для клика, и для вставленной в адресную строку ссылки).
      navigate(target.alias, target.eventId);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
    // navigate стабилен (useCallback []), client нужен заново после логина;
    // runAction пересоздаётся на рендер, но ведёт себя одинаково — свежесть
    // замыкания здесь ни на что не влияет.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, navigate]);


  // Сервер не выбран — показываем выбор вместо входа. Ранний return стоит
  // ПОСЛЕ всех хуков (см. комментарий про порядок хуков выше), поэтому
  // экран выбора живёт здесь, рядом с остальными ранними состояниями.
  if (!serverUrl) {
    return (
      <ServerPicker
        onSelect={() => {
          // Полная перезагрузка, а не setState: matrix-клиент, токены и
          // подписки уже могли создаться под прежний адрес. Перезагрузка —
          // единственный честный способ не тащить их на другой сервер.
          location.href = config.appUri;
        }}
      />
    );
  }

  if (phase === "starting" || phase === "connecting") {
    return (
      <Shell>
        <div style={s.centered}>
          {phase === "starting" ? t("app.sessionCheck") : t("app.serverConnect")}
        </div>
      </Shell>
    );
  }

  if (phase === "failed") {
    return (
      <Shell>
        <div style={s.centered}>
          <div>
            <p style={{ color: palette.danger }}>{tfmt("app.errorLogin", { error: error ?? "" })}</p>
            <button
              style={s.button}
              onClick={() => {
                logout();
                location.href = config.appUri;
              }}
            >{t("app.resetAndRestart")}</button>
          </div>
        </div>
      </Shell>
    );
  }

  if (phase === "anonymous") {
    return (
      <Shell>
        <div style={s.centered}>
          <div>
            {/* ПРИЧИНА ВЫХОДА (2026-09-26). Без этого блока принудительный
                разлогин выглядит как самопроизвольный вылет на экран входа:
                человек должен понять, что это сделали на сервере, а не
                «приложение сломалось». */}
            {sessionEnded && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ color: palette.danger, fontWeight: 600, margin: "0 0 4px" }}>
                  {t("app.sessionEndedTitle")}
                </p>
                <p style={{ margin: 0 }}>{t("app.sessionEndedBody")}</p>
              </div>
            )}
            <p>{t("app.corporateLogin")}</p>
            <button
              style={s.button}
              onClick={() => {
                setLoginError(null);
                setSessionEnded(false);
                // НЕ void: ошибка регистрации OIDC (MAS отверг
                // redirect_uri/client_uri, нет сети) обязана быть видна
                // под кнопкой, а не уходить в пустоту (баг «ничего не
                // происходит» на Android, 2026-09-20).
                beginLogin().catch((e) => {
                  setLoginError((e as Error).message);
                });
              }}
            >{t("app.login")}</button>
            {loginError && (
              <p style={{ color: palette.danger }}>
                {tfmt("app.errorLogin", { error: loginError })}
              </p>
            )}
            {/* Сервер видно до входа: перепутать прод со стендом на экране
                логина проще всего, а цена ошибки здесь максимальная. */}
            <p style={s.serverNote}>
              {tfmt("app.serverLabel", {
                server: `${serverLabel(serverUrl)} · ${serverUrl.replace(/^https:\/\//, "")}`,
              })}
              <button
                type="button"
                style={s.serverSwitch}
                onClick={() => {
                  clearHomeserverChoice();
                  setServerUrl(null);
                }}
              >{t("app.serverSwitch")}</button>
            </p>
          </div>
        </div>
      </Shell>
    );
  }

  // Первый sync ещё не завершён — список комнат будет пустым, и об этом
  // надо сказать, иначе выглядит как «у меня нет комнат».
  const loading = syncState !== "SYNCING" && syncState !== "PREPARED";

  // Комнату открываем только из состояний join/invite: leave/ban по чужой
  // ссылке — это ветка «недоступна», а не мёртвая лента (гоча постановки).
  const activeMembership = activeRoom?.getMyMembership();

  return (
    // Клиент кладём в контекст ОДИН раз здесь: аватары теперь качаются с
    // авторизацией, и client нужен в глубине дерева (строка чата, иконка
    // пространства, автор сообщения, список участников).
    <MatrixClientProvider value={client}>
    <Shell narrow={narrow}>
      {visibleIncomingCall && (
        <IncomingCallBanner
          call={visibleIncomingCall}
          onOpen={() => {
            // «Перейти» НЕ принимает звонок — просто ведёт в комнату, где
            // видна обычная плашка. Осталось для случая «хочу сначала
            // посмотреть, кто там и о чём», не отвечая.
            navigate(visibleIncomingCall.roomId);
            dismissIncomingCall();
            setDismissedCallRoom(visibleIncomingCall.roomId);
          }}
          onAccept={() => {
            // ПРИНЯТЬ ПРЯМО ИЗ ПЛАШКИ (Антон 12.09 09:43: «зачем куда-то
            // переходить?»). Порядок важен:
            //  1. navigate — держатель звонка (ActiveCallHolder) следует за
            //     ОТКРЫТОЙ комнатой, пока звонок не начат. Без перехода
            //     join() ушёл бы не в ту комнату.
            //  2. join() в ТОМ ЖЕ обработчике клика — «тёплый жест» для
            //     getUserMedia сохраняется: браузер отдаёт микрофон только
            //     в ответ на действие человека, и клик по этой кнопке им и
            //     является. Через setTimeout жест бы протух.
            // callObj берётся по факту: если держатель ещё не перепривязался
            // (комнату открыли этим же кликом), join придёт следующим
            // рендером — поэтому запоминаем намерение в pendingAcceptRoom.
            navigate(visibleIncomingCall.roomId);
            dismissIncomingCall();
            setDismissedCallRoom(visibleIncomingCall.roomId);
            if (callObj && callRoomId === visibleIncomingCall.roomId) {
              callObj.join();
            } else {
              setPendingAcceptRoom(visibleIncomingCall.roomId);
            }
          }}
          onDecline={() => {
            // Отказ по протоколу: звонящий узнает, что его отклонили.
            // Локально гасим сразу (внутри declineIncomingCall), баннер
            // скрываем тем же dismissedCallRoom — ждать сети не нужно.
            // client здесь всегда есть: баннер рисуется только после
            // входа. Проверка — чтобы типы не спорили, а не «на всякий».
            if (client) void declineIncomingCall(client, visibleIncomingCall);
            setDismissedCallRoom(visibleIncomingCall.roomId);
          }}
        />
      )}
      {actionError && (
        <div style={s.actionError} data-testid="action-error" role="alert">
          {actionError}
          <button
            type="button"
            style={{ ...s.actionButton, marginLeft: 12 }}
            onClick={() => setActionError(null)}
          >{t("app.hideActionError")}</button>
        </div>
      )}
      {/* Баннер «Вас добавили в …» удалён (Антон, 2026-09-27). */}
      <div style={s.body}>
        {/* Держатель звонка: смонтирован ВНЕ ErrorBoundary комнаты и потому
            переживает смену комнаты. Рендерит null, вся его работа —
            хук useCallJoin на уровне приложения. Раньше хук жил внутри
            RoomView и умирал вместе с ним, обрывая звонок. */}
        {client && (
          <ActiveCallHolder
            client={client}
            followRoomId={activeRoomId}
            onCallChange={handleCallChange}
          />
        )}
        {/* МОБИЛЬНЫЙ РЕЖИМ (2026-09-20): список показываем, только когда
            НЕТ открытой комнаты и НЕТ кабинета — иначе на 390px обе
            колонки не помещаются. Десктоп — как раньше, всегда. */}
        {(!narrow || (!activeRoomId && !profileOpen)) && (
        <RoomList
          invites={invites}
          joined={joined}
          spaces={spaces}
          totals={totals}
          activeRoomId={activeRoomId}
          onSelect={(roomId) => {
            // Клик по комнате при ОТКРЫТОМ кабинете закрывает кабинет и
            // переходит в комнату (BUG-2, 2026-09-13): иначе profileOpen
            // оставался true, ProfileView продолжал подменять колонку
            // комнаты, и человек видел «кликаю чат — ничего не происходит»,
            // что и было сутью жалобы «из кабинета невозможно выйти».
            setProfileOpen(false);
            navigate(roomId);
          }}
          // Клик по НАЙДЕННОМУ СООБЩЕНИЮ (поиск по содержимому,
          // 2026-09-24): та же навигация, что у permalink'а — комната плюс
          // eventId, который уйдёт в scrollToEventId и прокрутит ленту
          // к сообщению. Отдельной логики не заводим — механизм уже есть.
          onSelectEvent={(roomId, eventId) => {
            setProfileOpen(false);
            navigate(roomId, eventId);
          }}
          loading={loading}
          onActiveSpaceChange={setActiveSpaceId}
          onCreateRoom={() => setDialog({ kind: "create" })}
          onCreateDm={() => setDialog({ kind: "dm" })}
          // ВХОД В ЛИЧНЫЙ КАБИНЕТ — СВОЙ АВАТАР ВНИЗУ РЕЛЬСЫ (правка
          // Антона 12.09 12:26 п.1): самый нижний элемент левой рельсы,
          // ниже кнопки «＋». RoomList не знает про MatrixClient, поэтому
          // аватар собирается здесь (компонент OwnRailAvatar ниже знает
          // и профиль, и авторизованную загрузку media) и передаётся
          // готовым ReactNode.
          railBottom={
            client ? (
              <OwnRailAvatar client={client} onOpen={() => setProfileOpen(true)} />
            ) : undefined
          }
          // МОБИЛЬНАЯ ШАПКА (правка Антона 2026-09-20 19:17, п.2):
          // аватар слева → тот же кабинет, что и внизу рельсы на
          // десктопе; колесо ⚙️ справа → туда же (все настройки живут
          // в кабинете). RoomList рисует их только на narrow.
          mobileHeaderAvatar={
            client ? (
              <OwnRailAvatar client={client} onOpen={() => setProfileOpen(true)} />
            ) : undefined
          }
          onMobileOpenSettings={() => setProfileOpen(true)}
          onToggleFavourite={(r: RoomSummary) =>
            void runAction(async () => {
              if (!client) return;
              await setFavourite(client, r.roomId, !r.pinned);
            })
          }
          onInvite={(r) => setDialog({ kind: "invite", roomId: r.roomId, roomName: r.name })}
          onRoomLink={(r) => setDialog({ kind: "roomLink", roomId: r.roomId, roomName: r.name })}
          onLeave={(r) => setDialog({ kind: "leave", roomId: r.roomId, roomName: r.name })}
          onAcceptInvite={(r) =>
            void runAction(async () => {
              if (!client) return;
              await acceptInvite(client, r.roomId);
              // Сразу открываем комнату: иначе после нажатия «Принять» строка
              // просто переезжает вниз списка и выглядит как «пропала».
              navigate(r.roomId);
            })
          }
          onDeclineInvite={(r) =>
            setDialog({ kind: "decline", roomId: r.roomId, roomName: r.name })
          }
          onCreateSpace={
            // ТОЛЬКО АДМИНАМ (Антон 12.09 11:49 «available to admins only»).
            // undefined = кнопки нет вовсе. Признак админа измеряется
            // (PL>=100 в каком-либо пространстве), список MXID не зашит.
            client && mayCreateSpace(client)
              ? () => setDialog({ kind: "createSpace" })
              : undefined
          }
          onMoveToSpace={
            // ПЕРЕНОС ПОКАЗЫВАЕМ ТОЛЬКО ПРИ ПРАВАХ (спека п.4d, Антон 10:58:
            // «обычные пользователи с правом 0 этого делать не могут»).
            // undefined = пункт меню не рисуется вовсе. Это удобство:
            // настоящий запрет всё равно на сервере — он вернёт M_FORBIDDEN.
            client && spaces.some((sp) => mayManageSpaceChild(client, sp.roomId))
              ? (r) => setDialog({ kind: "moveRoom", roomId: r.roomId, roomName: r.name })
              : undefined
          }
          onSpaceInvite={(sp) =>
            setDialog({ kind: "inviteSpace", spaceId: sp.roomId, spaceName: sp.name })
          }
          // Права на управление пространством считаем ЗДЕСЬ и отдаём списку
          // флагом: RoomList не должен знать про MatrixClient (Антон 11:49).
          onSpaceMembers={(sp) =>
            setDialog({ kind: "spaceMembers", spaceId: sp.roomId, spaceName: sp.name })
          }
          canSpaceInvite={(sp) => (client ? mayInviteToSpace(client, sp.roomId) : false)}
          onSpaceRename={(sp) =>
            setDialog({ kind: "editSpace", spaceId: sp.roomId, spaceName: sp.name })
          }
          onSpaceLeave={(sp) =>
            // Пространство в Matrix — та же комната, поэтому переиспользуем
            // диалог выхода из комнаты, а не пишем копию с другим заголовком.
            setDialog({ kind: "leave", roomId: sp.roomId, roomName: sp.name })
          }
        />
        )}
        {client && profileOpen ? (
          // Граница ошибок вокруг КАБИНЕТА (BUG-2, 2026-09-13): сбой в
          // разделе (устройства / файлы / медиа) НЕ должен уносить весь
          // App вместе с кнопкой закрытия — иначе человек получает белый
          // экран без выхода, что и выглядело как «кабинет подвис».
          // onReset закрывает кабинет: App остаётся жив, человек видит
          // список чатов и может продолжить работу.
          <ErrorBoundary onReset={() => setProfileOpen(false)}>
          {/* ОБЁРТКА-КОЛОНКА ВОКРУГ КАБИНЕТА — ИСПРАВЛЕНИЕ ВЁРСТКИ
              (баг Антона 2026-09-21, Android 1080x2640).

              ЧТО БЫЛО СЛОМАНО: s.body (styles.ts:78) — это flex-СТРОКА
              (`display:flex` без flexDirection). ErrorBoundary рендерит
              своих детей как есть, поэтому мобильная плашка «‹ Назад» и
              сам ProfileView становились ДВУМЯ СОСЕДНИМИ КОЛОНКАМИ этой
              строки, а не шли друг под другом. Плашка `flex:0 0 auto`
              забирала ширину по содержимому (замерено: 195px из 393px),
              панели `flex:1 1 auto` доставалось 194px — ровно те «~40%
              справа, слева пустое поле» со скриншота. Дальше эффект шёл
              каскадом: колонка текста сжималась до 34px, и адрес
              @ab:algsuccess.com ложился в 7 строк почти по символу.

              ПОЧЕМУ ОБЁРТКА, А НЕ ПРАВКА s.body: s.body — общая строка
              «рельс | список | комната», и её направление менять нельзя,
              иначе поедет весь десктоп. Колонка нужна ровно здесь, где
              над панелью стоит своя шапка. На десктопе (narrow=false)
              обёртка содержит один ProfileView и ведёт себя как он сам:
              те же flex:1 1 auto / minWidth:0, замер до и после совпал. */}
          <div
            className="alg-profile-shell"
            style={{ flex: "1 1 auto", display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}
          >
          {/* МОБИЛЬНЫЙ КАБИНЕТ (спека 2026-09-20, этап 2): на ≤640px
              кабинет занимает весь экран, и списка не видно — добавляем
              кнопку «‹ Назад» к списку чатов, как в шапке чата. На
              десктопе её нет: список всегда виден слева. */}
          {narrow && (
            <div style={{ flex: "0 0 auto", display: "flex", alignItems: "center", padding: "6px 8px", borderBottom: `1px solid ${palette.borderSoft}`, background: palette.bg }}>
              <button
                type="button"
                className="alg-mobile-back"
                onClick={() => setProfileOpen(false)}
                data-testid="mobile-back-profile"
                aria-label={t("app.goBack")}
                title={t("app.goBack")}
              >
                <IconChevronLeft size={24} />
              </button>
              <span style={{ fontSize: 15, fontWeight: 600 }}>{t("app.profile")}</span>
            </div>
          )}
          <ProfileView
            client={client}
            notifPermission={notifPermission}
            onClose={() => setProfileOpen(false)}
            onLogout={async () => {
              // ЛОГАУТ ПЕРЕЕХАЛ В КАБИНЕТ (правка 12:26 п.2): верхняя
              // панель удалена целиком, и «Выйти» живёт последней строкой
              // профиля, красным и с подтверждением (внутри ProfileView).
              //
              // ПОРЯДОК ШАГОВ ПРИНЦИПИАЛЕН — оба первых шага требуют
              // ЖИВОГО access-токена, а logout() его стирает:
              //  1) removePusher — иначе pusher остался бы на сервере
              //     навсегда и следующий владелец устройства получал бы
              //     чужие уведомления. В браузере ничего не делает.
              //  2) endOwnSession — ЗАВЕРШЕНИЕ СЕССИИ НА СЕРВЕРЕ.
              //     До 25.09 этого шага НЕ БЫЛО ВОВСЕ: logout() чистил
              //     только localStorage, сессия в MAS оставалась живой, и
              //     устройство продолжало висеть в списке сессий — то
              //     самое «sign out не работает» (Антон 25.09 19:19).
              //  3) logout() + перезагрузка — локальная часть, последней.
              //
              // ОШИБКА НЕ ГЛОТАЕТСЯ: если сервер не смог завершить
              // сессию, мы БРОСАЕМ исключение наружу и НЕ чистим сессию
              // локально. Тихая локальная очистка при живой серверной
              // сессии хуже честного отказа: человек думает, что вышел,
              // а его токен продолжает действовать. Текст показывает
              // LogoutRow в ProfileView.
              await unregisterNativePush(client).catch(() => {
                /* pusher не снялся (сеть/сессия): не повод запереть
                   человека в аккаунте — главное действие ниже. */
              });
              await endOwnSession(client);
              logout();
              // Сброс выбора сервера ДО перезагрузки — тем же путём, что
              // и «сменить сервер» (clearHomeserverChoice). После reload
              // selectedHomeserverUrl() вернёт null и App покажет выбор
              // сервера, а не форму логина (Антон, 05.10). Живую сессию
              // не задевает: до логаута ключ не трогает никто.
              clearHomeserverChoice();
              location.reload();
            }}
            serverName={serverLabel(serverUrl)}
            serverUrl={serverUrl}
            onSwitchServer={() => {
              // ВАЖНО: сессию НЕ трогаем. Уходим только с выбора сервера,
              // чтобы возврат на прежний сервер не требовал входа заново.
              clearHomeserverChoice();
              location.href = config.appUri;
            }}
          />
          </div>
          </ErrorBoundary>
        ) : client && activeRoom && (activeMembership === "join" || activeMembership === "invite") ? (
          // Граница ошибок вокруг ОТКРЫТОЙ КОМНАТЫ, а не вокруг всего
          // приложения: сбой в ленте/треде не должен уносить список чатов.
          // key по комнате — при переходе в другую комнату состояние
          // ошибки сбрасывается само, без ручного reset.
          <ErrorBoundary key={activeRoom.roomId} onReset={() => navigate(null)}>
          <RoomView
            client={client}
            room={activeRoom}
            /* Мобильный режим (2026-09-20): на ≤640px чат занимает весь
               экран, и список не виден — единственный способ вернуться
               к нему из интерфейса (не из истории) — кнопка «‹ Назад»
               в шапке чата. Проп передаём только на narrow; на десктопе
               кнопки нет (там список всегда виден). */
            onMobileBack={narrow ? () => navigate(null) : undefined}
            /* Звонок приходит СВЕРХУ (держатель выше живёт вне ErrorBoundary
               и потому переживает смену комнаты). RoomView сам больше
               useCallJoin не вызывает. */
            callJoin={callObj && callRoomId === activeRoom.roomId ? callObj : null}
            /* Свёрнутый звонок: RoomView НЕ рисует полный экран, открывая
               ленту и композер (дополнение 2026-09-12 09:20). Флаг
               относится только к звонку ЭТОЙ комнаты — в чужих комнатах
               полного экрана и так нет, а мини-фрейм рисует App. */
            callMinimized={callMinimized && callRoomId === activeRoom.roomId}
            onMinimizeCall={minimizeCall}
            scrollToEventId={pendingEventId}
            onScrollToEventHandled={() => setPendingEventId(null)}
            onInvite={() =>
              setDialog({
                kind: "invite",
                roomId: activeRoom.roomId,
                roomName: activeRoom.name || activeRoom.roomId,
              })
            }
            onRoomLink={() =>
              setDialog({
                kind: "roomLink",
                roomId: activeRoom.roomId,
                roomName: activeRoom.name || activeRoom.roomId,
              })
            }
            onAddParticipantFromDm={() => {
              // DM stays a DM (Anton 2026-09-17 08:19): pick a third person,
              // a new group of three is created, and the call moves there.
              const me = client.getUserId();
              const partner = activeRoom
                .getJoinedMembers()
                .map((m) => m.userId)
                .find((id) => id !== me);
              if (!partner) return;
              setDialog({
                kind: "callAddFromDm",
                roomId: activeRoom.roomId,
                roomName: activeRoom.name || activeRoom.roomId,
                partnerId: partner,
                onCreated: (newRoomId) => {
                  // Leave the DM call, open the new group, and join its call
                  // as soon as the holder rebinds (same path as accepting an
                  // incoming call from the banner). The partner gets rung in
                  // the new group and is auto-joined there.
                  callObj?.leave();
                  navigate(newRoomId);
                  setPendingAcceptRoom(newRoomId);
                },
              });
            }}
            onLeave={() =>
              setDialog({
                kind: "leave",
                roomId: activeRoom.roomId,
                roomName: activeRoom.name || activeRoom.roomId,
              })
            }
            onAcceptInvite={() =>
              void runAction(async () => {
                await acceptInvite(client, activeRoom.roomId);
              })
            }
            onDeclineInvite={() =>
              setDialog({
                kind: "decline",
                roomId: activeRoom.roomId,
                roomName: activeRoom.name || activeRoom.roomId,
              })
            }
            onToggleFavourite={() =>
              void runAction(async () => {
                const pinned = Boolean(activeRoom.tags?.["m.favourite"]);
                await setFavourite(client, activeRoom.roomId, !pinned);
              })
            }
          />
          </ErrorBoundary>
        ) : unavailableRoomId && client ? (
          /* Не участник открыл ссылку на комнату (или любой адрес комнаты,
             которой у нас нет): «Войти в комнату», если правило входа
             позволяет, иначе «Комната закрытая, попросите приглашение».
             Заменяет прежнюю заглушку «Комната недоступна». */
          <RoomLinkGate
            key={unavailableRoomId}
            client={client}
            target={unavailableRoomId}
            via={routeVia}
            onJoined={(id) => navigate(id)}
            onBack={() => navigate(null)}
          />
        ) : !narrow ? (
          /* Мобильный режим (2026-09-20): когда комната НЕ открыта, список
             уже занимает весь экран — заглушка «выберите комнату» рядом с
             ним на 390px была бы второй колонкой в полметра шириной. На
             десктопе она на месте: там список узкий, и правая часть
             пустовать не должна. */
          <section style={s.main}>
            <div style={s.centered}>{t("app.selectRoom")}</div>
          </section>
        ) : null}
      </div>

      {client && (
        <RoomDialogs
          client={client}
          request={dialog}
          activeSpaceId={activeSpaceId}
          onClose={() => setDialog(null)}
          onRoomCreated={(roomId) => navigate(roomId)}
          onRoomLeft={(roomId) => {
            // Оставлять открытой комнату, из которой только что вышли, нельзя:
            // лента будет живой на вид, а отправка уже не работать.
            if (activeRoomId === roomId) navigate(null);
          }}
        />
      )}
      {/* Диалог обновления Android-приложения — НАШИМИ компонентами и
          токенами темы (требование Антона: «Весь внешний вид должен быть
          единообразен»). update не null только в нативной оболочке:
          браузер/десктоп сюда не доходят никогда. «Обновить» открывает
          url штатным путём (системный загрузчик Android сам скачает APK);
          диалог закрываем, чтобы не висеть поверх переключения в
          загрузчик. mandatory → кнопки «Позже» нет, окно не закрывается
          ни по Esc, ни по клику по затемнению (логика внутри UpdateDialog). */}
      {update && (
        <UpdateDialog
          update={update}
          sizeLabel={formatUpdateSize(update.size, currentLocale())}
          onUpdate={() => {
            openUpdateUrl(update.url);
            if (!update.mandatory) setUpdate(null);
          }}
          onLater={() => setUpdate(null)}
        />
      )}
      {/* МИНИ-ЗВОНОК (критичный баг 2026-09-12, Антон: «звонок должен
          отсоединяться, уходить как в picture-in-picture, отдельным
          висящим фреймом»). Виден когда звонок ИДЁТ (in-call) и ЛИБО
          открытая комната — НЕ комната звонка, ЛИБО звонок свёрнут
          кнопкой «написать» в той же комнате (дополнение 09:20:
          свёрнутый вид открывает обычный композер).
          joining/error мини-фрейм не показывает: у этих состояний своя
          плашка в комнате звонка, а в чужой комнате моё «подключаемся…»
          человеку без надобности. */}
      {callObj &&
        callRoomId &&
        callObj.state === "in-call" &&
        (callRoomId !== activeRoomId || callMinimized) &&
        client?.getRoom(callRoomId) && (
          <CallMiniFrame
            client={client}
            call={callObj}
            roomName={client.getRoom(callRoomId)!.name || callRoomId}
            onReturn={() => {
              // «Вернуться» из СВЁРНУТОГО вида в той же комнате: мы уже
              // в комнате звонка, navigate не нужен (и вреден: он бы
              // переоткрыл комнату). Просто снимаем флаг — RoomView
              // снова рисует полный экран. Из чужой комнаты — старое
              // поведение: переход в комнату звонка + разворот.
              setCallMinimized(false);
              if (callRoomId !== activeRoomId) navigate(callRoomId);
            }}
          />
        )}
    </Shell>
    </MatrixClientProvider>
  );
}

function Shell({ children, narrow = false }: { children: React.ReactNode; narrow?: boolean }) {
  // ВЕРХНИЙ БЛОК УДАЛЁН ЦЕЛИКОМ (правка Антона 12.09 12:26 п.3): плашки
  // с названием проекта и кнопкой выхода больше нет — не спрятана, а
  // убрана из разметки. Вход в кабинет — аватар внизу левой рельсы,
  // выход — последняя строка кабинета. Освободившаяся высота отдана
  // списку чатов: s.app — колонка на 100vh, дети растягиваются flex'ом,
  // пересчёт высот не нужен, пустой полосы не остаётся (проверено по
  // styles.ts: s.body имеет flex 1 1 auto от оставшейся высоты).
  //
  // МОБИЛЬНАЯ РАСКЛАДКА (2026-09-20): класс `alg-mobile` на корне включает
  // весь @media-блок mobileCss (≤640px), а сам mobileCss подключаем один
  // раз здесь — точно так же, как roomListCss подключает RoomList.
  return (
    <div style={s.app} className={narrow ? "alg-mobile" : undefined}>
      <style>{mobileCss}</style>
      {children}
    </div>
  );
}

/**
 * Свой аватар в НИЗУ левой рельсы — вход в личный кабинет (правка
 * Антона 12.09 12:26 п.1: «самым нижним элементом левой рельсы, ниже
 * кнопки «＋»»).
 *
 * Живёт в App, а не в RoomList, потому что ему нужен MatrixClient:
 * профиль (displayname/avatar_url) читается с сервера, а сама картинка
 * грузится через авторизованный blob (наш Synapse отдаёт media только с
 * Bearer — см. matrix/media.ts). RoomList клиента не знает и получает
 * готовый ReactNode пропом railBottom.
 *
 * Размер — тот же railIcon 40px, что у иконок спейсов: рельс — единая
 * линейка, и чужеродный крупный аватар внизу выглядел бы вклеенным.
 * Картинки нет (или не загрузилась) — инициалы на детерминированном
 * цвете (та же colorOf, что у спейсов и комнат).
 */
export function OwnRailAvatar({ client, onOpen }: { client: MatrixClient; onOpen: () => void }) {
  const t = useT();
  const tfmt = useTF();
  const [avatarMxc, setAvatarMxc] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  const userId = client.getUserId() ?? "";

  useEffect(() => {
    let cancelled = false;
    fetchOwnProfile(client)
      .then((p) => {
        if (cancelled) return;
        setAvatarMxc(p.avatarMxc);
        setDisplayName(p.displayName);
      })
      .catch(() => {
        // Профиль не прочитался — остаются инициалы из MXID. Кнопка входа
        // в кабинет обязана работать и при упавшем /profile.
      });
    return () => {
      cancelled = true;
    };
  }, [client]);
  useEffect(() => setBroken(false), [avatarMxc]);

  /* АВАТАР — через useAuthedAvatar (уточнение Антона 2026-09-20 19:35),
     а НЕ useAuthedMedia. Три причины ровно те же, что в списке комнат
     и рельсе спейсов:
       1) thumbnail с сервера: ≥2× display size × DPR, method=crop —
          кружок не размыт на Retina (правка f492a1a от 2026-09-18);
       2) общий кэш аватаров на весь клиент: мобильная шапка
          размонтируется при переходе «список ↔ чат», и без кэша blob:-URL
          умирал при каждом unmount → на обратном пути человек видел
          пустые инициалы, пока fetch не успеет отработать (это и был
          репорт Антона «аватар в шапке пустой»);
       3) тот же код, что в Row/RailIcon/MobileSpaceIcon — один путь,
          один кэш, одни граничные случаи.
     size.railIcon (40px) — отображаемый размер кнопки в рельсе и в
     мобильной шапке одинаковый. Фолбэк на null/ошибку — инициалы на
     цветном круге ниже, как в остальном приложении. */
  const url = useAuthedAvatar(client, avatarMxc, size.railIcon);

  const name = displayName ?? userId;
  const initials = (() => {
    const clean = name.replace(/^@/, "");
    const words = clean.split(/[\s_\-.:]+/).filter(Boolean);
    if (words.length === 0) return "?";
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  })();

  return (
    <button
      type="button"
      className="alg-rail-btn"
      onClick={onOpen}
      title={tfmt("profile.titleWithName", { name })}
      aria-label={t("app.profile")}
      data-testid="open-profile"
      style={{
        // Прижать к НИЗУ рельсы: рельс — flex-колонка, и marginTop:auto
        // отправляет аватар в самый низ, ниже «＋» (правка п.1 дословно).
        marginTop: "auto",
        background: "transparent",
        border: "none",
        padding: 0,
        cursor: "pointer",
        flex: "0 0 auto",
      }}
    >
      {url && !broken ? (
        <img
          src={url}
          alt=""
          style={s.railIconInner}
          onError={() => setBroken(true)}
          data-testid="rail-own-avatar-img"
        />
      ) : (
        <div
          style={{ ...s.railIconInner, background: colorOf(userId || "?") }}
          aria-hidden
          data-testid="rail-own-avatar-stub"
        >
          {initials}
        </div>
      )}
    </button>
  );
}

/**
 * Держатель звонка на уровне приложения (критичный баг 2026-09-12,
 * Антон: «сменил комнату — звонок сбрасывается»).
 *
 * Ничего не рендерит — вся его работа это хук useCallJoin, который
 * теперь живёт ВНЕ комнаты и переживает смену комнаты. Раньше хук жил
 * внутри RoomView и умирал вместе с ним (ErrorBoundary key={roomId}),
 * а эффект очистки в rtc.ts обрывал звонок.
 *
 * СХЕМА ПРИВЯЗКИ (простой вариант, выбран вместо claim-протокола с
 * комнатой-заглушкой — тот требовал фальшивого объекта Room и
 * отложенного join, а здесь всё прямо):
 *   • ПОКА ЗВОНКА НЕТ держатель СЛЕДУЕТ за открытой комнатой
 *     (followRoomId от App). Поэтому кнопка звонка в шапке вызывает
 *     join() УЖЕ существующего объекта этой комнаты — по тёплому жесту,
 *     без промежуточных setState и ожидания перепривязки.
 *   • КАК ТОЛЬКО звонок начался (state ушёл из idle), комната
 *     ЗАМОРАЖИВАЕТСЯ внутри держателя до конца звонка: переходы по
 *     списку чатов его больше не трогают, и звонок живёт, пока человек
 *     ходит по комнатам (мини-фрейм рисует App).
 *   • Когда звонок завершился или упал с ошибкой (state idle/error) —
 *     заморозка снимается, держатель снова следует за открытой комнатой.
 *
 * ПЕРЕСОЗДАНИЕ ХУКА при следовании (key по комнате): в idle это дёшево
 * и безопасно — teardown там no-op (ни членства, ни LiveKit ещё нет),
 * а persistOnUnmount=true запрещает очистку по размонтированию.
 *
 * ДВОЙНОЙ ВХОД исключён конструкцией: это ЕДИНСТВЕННОЕ место, где
 * вызывается useCallJoin (RoomView свой больше не создаёт), а сам хук
 * повторный join при joinedRef/livekitRef игнорирует (rtc.ts).
 */
function ActiveCallHolder({
  client,
  followRoomId,
  onCallChange,
}: {
  client: MatrixClient;
  /** Открытая комната: за ней следуем, пока звонок не начат. */
  followRoomId: string | null;
  /** Отдаёт актуальный объект звонка и его комнату наверх. */
  onCallChange: (call: CallJoin | null, roomId: string | null) => void;
}) {
  // Замороженная комната звонка (null = заморозки нет, следуем за
  // открытой). State, а не ref: при заморозке/разморозке надо
  // перерендерить Bound с новым key.
  const [frozenRoom, setFrozenRoom] = useState<Room | null>(null);
  const followRoom = followRoomId ? client.getRoom(followRoomId) : null;
  const boundRoom = frozenRoom ?? followRoom;

  // Спим, когда привязываться не к чему (комната не выбрана и звонка
  // нет): useCallJoin без комнаты вызвать нельзя, а условный КОМПОНЕНТ
  // (не хук) это позволяет. Во время звонка сюда не попадаем: заморозка
  // держит комнату, даже если человек закрыл её (navigate(null)).
  if (!boundRoom) {
    return <ActiveCallSleeping onCallChange={onCallChange} />;
  }
  return (
    <ActiveCallBound
      key={boundRoom.roomId}
      client={client}
      room={boundRoom}
      frozen={frozenRoom !== null}
      onFreeze={setFrozenRoom}
      onUnfreeze={() => setFrozenRoom(null)}
      onCallChange={onCallChange}
    />
  );
}

/** Держатель без комнаты: звонка нет, сообщаем наверх null. */
function ActiveCallSleeping({
  onCallChange,
}: {
  onCallChange: (call: CallJoin | null, roomId: string | null) => void;
}) {
  useEffect(() => {
    onCallChange(null, null);
  });
  return null;
}

/** Привязанный держатель: хук на конкретной комнате. */
function ActiveCallBound({
  client,
  room,
  frozen,
  onFreeze,
  onUnfreeze,
  onCallChange,
}: {
  client: MatrixClient;
  room: Room;
  frozen: boolean;
  onFreeze: (room: Room) => void;
  onUnfreeze: () => void;
  onCallChange: (call: CallJoin | null, roomId: string | null) => void;
}) {
  // persistOnUnmount=true: очистка по размонтированию в rtc.ts
  // ОТКЛЮЧЕНА. Размонтирование этого компонента теперь значит одно из
  // двух: следование за открытой комнатой в idle (чистить нечего) или
  // реальный выход из приложения — для него в rtc.ts слушатель
  // beforeunload. «Выйти»/«Отменить»/автовыходы («никто не ответил»,
  // «все ушли») зовут leave() → тот же teardown, что и раньше.
  const call = useCallJoin(client, room, true);

  // Заморозка/разморозка комнаты по состоянию звонка. error тоже
  // размораживает: упавший вход не должен прибивать держателя к комнате
  // и мешать позвонить из другой. Плашка ошибки при этом не теряется:
  // она живёт в RoomView той комнаты, а держатель после разморозки
  // пересоздаст хук уже на новой открытой комнате.
  useEffect(() => {
    if (call.state === "idle" || call.state === "error") {
      if (frozen) onUnfreeze();
    } else if (!frozen) {
      onFreeze(room);
    }
  }, [call.state, frozen, room, onFreeze, onUnfreeze]);

  // Объект звонка — новый на каждый рендер (rtc.ts собирает его
  // литералом), поэтому эффект БЕЗ массива зависимостей: App должен
  // видеть свежие поля (state, participants, videoTiles). setState в
  // родителе из эффекта законен: React откладывает перерендер.
  useEffect(() => {
    onCallChange(call, room.roomId);
  });
  return null;
}

/**
 * Мини-звонок: компактный висящий фрейм поверх интерфейса, пока человек
 * ушёл из комнаты звонка (критичный баг 2026-09-12) ИЛИ свернул звонок
 * кнопкой «написать» в той же комнате (дополнение 09:20). Это свой
 * DOM-фрейм, НЕ браузерный Picture-in-Picture (этап 5, здесь запрещён).
 *
 * ПЕРЕТАСКИВАНИЕ (дополнение 2026-09-12 09:20, Антон: «оно приковано к
 * одному месту, если сделаем его отвязку — будет вообще идеально»).
 * Прежнее «перетаскивать не нужно» ОТМЕНЕНО. Реализация:
 *  - тащим за любое место фрейма КРОМЕ кнопок: в pointerdown проверяем
 *    e.target.closest("button") — клик по кнопке drag не начинает, и
 *    кнопки продолжают работать как раньше;
 *  - pointer events + setPointerCapture: один обработчикный цикл вместо
 *    mousemove на document, и тач-экраны получают перетаскивание
 *    тривиально тем же кодом (проверка постановки «если тривиально —
 *    сделай»: да, pointer events это дают бесплатно);
 *  - позиция — ЛЕВЫЙ ВЕРХНИЙ угол (pos), а не right/bottom: при drag
 *    считать от правого края неудобно и на ресайзе ведёт себя хуже;
 *  - границы: кламп в [0, window.inner - размер фрейма] на каждом
 *    движении И на resize окна — фрейм не вылезает за окно ни при
 *    перетаскивании, ни при уменьшении окна;
 *  - позиция хранится в localStorage (ключ ниже): переживает и
 *    сворачивания, и перезагрузку страницы. Битую запись (NaN, вне
 *    окна) игнорируем и возвращаемся в дефолтный правый нижний угол.
 *
 * Вид: тёмная панель в стиле экрана звонка (та же палитра, что у
 * CallScreen): превью своей камеры (если включена) или аватар-заглушка,
 * имя комнаты, микрофон, камера, завершить — и кнопка «Вернуться в
 * звонок», переводящая в комнату звонка (или разворачивающая свёрнутый
 * звонок, если мы уже в ней).
 */
/** Ключ localStorage под позицию мини-фрейма. */
const CALL_MINI_POS_KEY = "alg.callMiniPos";
/** Ширина фрейма — константа, нужна и стилю, и клампу границ. */
const CALL_MINI_WIDTH = 220;

/** Прочитать сохранённую позицию; null — дефолтный угол. */
function readMiniPos(): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(CALL_MINI_POS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as { x?: unknown; y?: unknown };
    if (typeof p.x !== "number" || typeof p.y !== "number") return null;
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return null;
    return { x: p.x, y: p.y };
  } catch {
    // localStorage недоступен (приватный режим, запрет) — живём без
    // запоминания, перетаскивание в пределах сессии работает.
    return null;
  }
}

/** Вжать позицию в видимую область окна. */
function clampMiniPos(x: number, y: number, height: number): { x: number; y: number } {
  const maxX = Math.max(0, window.innerWidth - CALL_MINI_WIDTH);
  const maxY = Math.max(0, window.innerHeight - height);
  return {
    x: Math.min(Math.max(0, x), maxX),
    y: Math.min(Math.max(0, y), maxY),
  };
}

function CallMiniFrame({
  client,
  call,
  roomName,
  onReturn,
}: {
  client: MatrixClient;
  call: CallJoin;
  roomName: string;
  onReturn: () => void;
}) {
  const t = useT();
  // Мини-фрейм повторяет нормальную видеологику звонка: главным всегда
  // показываем ЧУЖОЙ поток (remote screen share, затем текущего говорящего,
  // затем первую удалённую камеру). Собственный поток — только маленький
  // PiP поверх. Прежняя реализация выбирала исключительно localTile, из-за
  // чего после сворачивания человек видел самого себя вместо собеседника.
  const remoteTiles = call.videoTiles.filter((tile) => !tile.local);

  /* АКТИВНЫЙ ГОВОРЯЩИЙ В МИНИ-ФРЕЙМЕ (Антон 2026-09-18, п.6: «PiP должен
     показывать активного говорящего — того, кто в основном окне подсвечен
     красной рамкой, — переключаясь по мере смены говорящего. Сейчас
     выбирает случайного и держит»).

     Почему прежний код не работал, хотя `speaking` в нём уже читался.
     `speaking` берётся из `activeSpeakerIdentity` (rtc.ts), а он приходит из
     LiveKit RoomEvent.ActiveSpeakersChanged и в паузах между фразами
     становится ПУСТЫМ. Тогда цепочка `??` проваливалась до `remoteTiles[0]`
     — до случайного участника, который и залипал на экране: именно это
     Антон и видел. Плюс участник без камеры плитки не имеет вовсе, и
     говорящий audio-only не мог стать главным никогда.

     Решение — ЛИПКИЙ выбор: помним последнего РЕАЛЬНОГО удалённого
     говорящего и держим его, пока не заговорит следующий. Пауза в речи
     больше не перекидывает картинку, а смена говорящего — перекидывает.
     Источник тот же `activeSpeakerIdentity`, что и у красной рамки в основном
     окне, поэтому мини-фрейм и большой экран показывают одного человека.
     Сигналинг MatrixRTC/LiveKit не затронут. */
  const speakingRemoteIdentity =
    call.callParticipants.find((p) => p.speaking && !p.local)?.identity ?? null;
  const [stickySpeaker, setStickySpeaker] = useState<string | null>(null);
  useEffect(() => {
    if (speakingRemoteIdentity) setStickySpeaker(speakingRemoteIdentity);
  }, [speakingRemoteIdentity]);
  // Говоривший вышел из звонка — забываем его, иначе мини-фрейм смотрел бы
  // на пустое место вместо оставшихся собеседников.
  const remoteIdentities = call.callParticipants
    .filter((p) => !p.local)
    .map((p) => p.identity)
    .join("\u0000");
  useEffect(() => {
    setStickySpeaker((current) =>
      current && remoteIdentities.split("\u0000").includes(current) ? current : null,
    );
  }, [remoteIdentities]);

  const activeSpeaker = stickySpeaker ?? speakingRemoteIdentity;
  const speakerTile = activeSpeaker
    ? remoteTiles.find(
        (tile) => tile.participantIdentity === activeSpeaker && tile.kind !== "screen",
      ) ??
      remoteTiles.find((tile) => tile.participantIdentity === activeSpeaker) ??
      null
    : null;
  const screenTile = remoteTiles.find((tile) => tile.kind === "screen") ?? null;
  const speakerParticipant = activeSpeaker
    ? call.callParticipants.find((p) => p.identity === activeSpeaker) ?? null
    : null;

  /* ЧТО ИМЕННО ПОКАЗЫВАЕМ, в порядке приоритета:
       1) чужая демонстрация экрана — её показывают, чтобы на неё смотрели;
       2) видео активного говорящего;
       3) АВАТАР активного говорящего, если камера у него выключена;
       4) любая удалённая камера — пока никто не говорил ни разу.

     Пункт 3 важнее пункта 4 именно в таком порядке. Если сделать наоборот,
     получается ровно та ложь, ради которой всё и затевалось: говорит А без
     камеры, а в PiP молча сидит Б, потому что у НЕГО камера есть. */
  const primaryRemote =
    screenTile ?? speakerTile ?? (speakerParticipant ? null : remoteTiles[0] ?? null);
  // Аватар вместо видео — только когда говорящий известен, но плитки у него нет.
  // В аудиозвонке без этого мини-фрейм оставался безликим квадратом с двумя
  // буквами названия комнаты, и понять «кто сейчас говорит» было невозможно.
  const audioOnlySpeaker = !primaryRemote && speakerParticipant ? speakerParticipant : null;
  /* Подпись обязана называть ТОГО, КТО НА ЭКРАНЕ, а не того, кого мы считаем
     говорящим: иначе подпись и лицо расходятся, а это хуже отсутствия подписи. */
  const shownLabel = primaryRemote?.label ?? audioOnlySpeaker?.label ?? null;
  const localTile =
    call.videoTiles.find((tile) => tile.local && tile.kind !== "screen") ??
    call.videoTiles.find((tile) => tile.local) ??
    null;

  const rootRef = useRef<HTMLDivElement | null>(null);
  /** null = дефолтный правый нижний угол (стили right/bottom). */
  const [pos, setPos] = useState<{ x: number; y: number } | null>(() => readMiniPos());
  /** true, пока идёт перетаскивание (для курсора grabbing). */
  const [dragging, setDragging] = useState(false);
  /** Смещение точки захвата от левого верхнего угла фрейма. */
  const grabRef = useRef<{ dx: number; dy: number } | null>(null);

  // Реальная высота фрейма нужна клампу по вертикали: она зависит от
  // контента (превью 16:9 от ширины + строки), считать её руками хрупко.
  const heightOf = () => rootRef.current?.offsetHeight ?? 0;

  // Стартовая нормализация: сохранённая позиция могла остаться от окна
  // другого размера — вжимаем в текущее окно один раз при монтировании.
  useEffect(() => {
    setPos((p) => (p ? clampMiniPos(p.x, p.y, heightOf()) : p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ресайз окна: фрейм обязан остаться внутри видимой области (постановка).
  useEffect(() => {
    const onResize = () => setPos((p) => (p ? clampMiniPos(p.x, p.y, heightOf()) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    // Тащим НЕ за кнопку: клик по кнопкам (микрофон/камера/трубка/
    // «Вернуться») остаётся кликом — это требование постановки. Проверка
    // по closest("button"), а не по списку testid: новая кнопка внутри
    // фрейма автоматически получит то же исключение.
    if (e.target instanceof Element && e.target.closest("button")) return;
    // Только основная кнопка мыши (или касание): правый клик — контекстное
    // меню, отбирать его незачем.
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    grabRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    setDragging(true);
    // Capture: дальнейшие move/up приходят нам даже при выходе курсора
    // за пределы фрейма — без capture быстрый рывок ронял бы drag.
    rootRef.current?.setPointerCapture(e.pointerId);
    // Фрейм мог стоять в дефолтном углу (pos=null): переводим в явную
    // позицию от левого верха, чтобы дальше двигать от неё.
    setPos((p) => p ?? { x: rect.left, y: rect.top });
    e.preventDefault();
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const grab = grabRef.current;
    if (!grab) return;
    setPos(clampMiniPos(e.clientX - grab.dx, e.clientY - grab.dy, heightOf()));
  };

  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!grabRef.current) return;
    grabRef.current = null;
    setDragging(false);
    rootRef.current?.releasePointerCapture(e.pointerId);
    // Запоминаем финальную позицию: между сворачиваниями и (через
    // localStorage) между перезагрузками.
    setPos((p) => {
      if (p) {
        try {
          localStorage.setItem(CALL_MINI_POS_KEY, JSON.stringify(p));
        } catch {
          // Записать не вышло — не страшно: позиция в state жива.
        }
      }
      return p;
    });
  };

  return (
    <div
      ref={rootRef}
      data-testid="call-mini"
      /* alg-call-miniframe: на ≤640px мобильный CSS сжимает фрейм и
         прибивает его вниз над композером (спека 2026-09-20, этап 4).
         Класс ни на что не влияет на десктопе. */
      className="alg-call-miniframe"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      style={{
        position: "fixed",
        ...(pos
          ? { left: pos.x, top: pos.y }
          : { right: space.xl, bottom: space.xl }),
        zIndex: 40,
        width: CALL_MINI_WIDTH,
        borderRadius: radius.md,
        // Тот же тёмный фон, что у экрана звонка (color.text): звонок —
        // одна сущность в любом представлении, и светлая плашка мини-
        // фрейма выглядела бы посторонней на тёмном видео.
        background: color.text,
        color: "#fff",
        boxShadow: "0 8px 28px rgba(0,0,0,0.35)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        // Весь фрейм — ручка перетаскивания (кроме кнопок, они ловятся
        // в pointerdown раньше). touch-action:none: без него тач-перетаскивание
        // отдаёт жест прокрутке страницы раньше, чем придёт pointermove.
        cursor: dragging ? "grabbing" : "grab",
        touchAction: "none",
        userSelect: "none",
      }}
    >
      {/* Верхняя строка: название комнаты звонка + «Вернуться». */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: space.sm,
          padding: `${space.sm}px ${space.md}px`,
          fontSize: 12,
        }}
      >
        {/* Индикатор демонстрации экрана (задача 2026-09-13): кнопку
            шеринга в компактный ряд НЕ добавляем — там тесно и управление
            живёт на полном экране звонка. Но если экран ЖИВОЙ, мини-фрейм
            обязан это показывать: забытая демонстрация — приватность, а
            не косметика, поэтому значок горит постоянно, пока screenOn. */}
        {call.screenOn && (
          <span
            aria-label={t("ui.id_t_demonstraciya_vashego_ekrana")}
            title={t("ui.id_t_demonstraciya_vashego_ekrana")}
            /* Белый, как все иконки мини-фрейма: красный зарезервирован
               за микрофоном (правило Антона 12.09 10:29), а видимость
               здесь даёт САМО ПРИСУТСТВИЕ значка, а не его цвет. */
            style={{ display: "inline-flex", flex: "0 0 auto", color: "#fff" }}
          >
            <IconScreenShare size={14} />
          </span>
        )}
        <span
          style={{
            flex: "1 1 auto",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            opacity: 0.85,
          }}
          title={roomName}
        >
          {roomName}
        </span>
        <button
          type="button"
          data-testid="call-mini-return"
          onClick={onReturn}
          title={t("ui.vernutsya_v_zvonok")}
          style={{
            flex: "0 0 auto",
            font: "inherit",
            fontSize: 12,
            padding: "4px 10px",
            borderRadius: radius.sm,
            border: "none",
            background: color.accent,
            color: color.textOnAccent,
            cursor: "pointer",
          }}
        >{t("app.goBack")}</button>
      </div>

      {/* Мини-раскладка: удалённый участник/говорящий занимает основу,
          своё видео — маленький PiP поверх и никогда не вытесняет его. */}
      <div style={{ position: "relative", aspectRatio: "16/9", background: "#000" }}>
        {primaryRemote ? (
          <MiniVideoElement
            stream={primaryRemote.stream}
            muted={false}
            fit={primaryRemote.kind === "screen" ? "contain" : "cover"}
          />
        ) : audioOnlySpeaker ? (
          /* Говорящий без камеры — его аватар и имя (п.6). Меняется вместе
             с говорящим, как и видеоплитка выше. */
          <MiniSpeakerAvatar
            client={client}
            label={audioOnlySpeaker.label}
            avatarMxc={audioOnlySpeaker.avatarMxc}
            identity={audioOnlySpeaker.identity}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "grid",
              placeItems: "center",
              background: color.accent,
              color: color.textOnAccent,
              fontSize: 22,
              fontWeight: 700,
              userSelect: "none",
            }}
          >
            {roomName.replace(/^[#!@]/, "").slice(0, 2).toUpperCase()}
          </div>
        )}
        {/* ПОДПИСЬ ГОВОРЯЩЕГО над видео. Без неё переключение между двумя
            незнакомыми лицами в маленьком окне читается как глюк, а не как
            смена говорящего. Не показываем над демонстрацией экрана: там важно
            содержимое, и плашка его закрывала бы. */}
        {primaryRemote?.kind !== "screen" && shownLabel && (
          <div
            data-testid="call-mini-speaker-name"
            style={{
              position: "absolute",
              left: space.sm,
              bottom: space.sm,
              maxWidth: "60%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 11,
              lineHeight: 1.3,
              padding: "2px 6px",
              borderRadius: radius.sm,
              background: "rgba(0,0,0,0.55)",
              color: "#fff",
              pointerEvents: "none",
            }}
          >
            {shownLabel}
          </div>
        )}
        {primaryRemote && localTile && (
          <div
            data-testid="call-mini-local-pip"
            style={{
              position: "absolute",
              right: space.sm,
              bottom: space.sm,
              width: "32%",
              minWidth: 58,
              aspectRatio: "16/9",
              overflow: "hidden",
              borderRadius: radius.sm,
              background: "#000",
              boxShadow: "0 2px 10px rgba(0,0,0,0.65)",
              border: "1px solid rgba(255,255,255,0.55)",
            }}
          >
            <MiniVideoElement
              stream={localTile.stream}
              muted
              fit={localTile.kind === "screen" ? "contain" : "cover"}
            />
          </div>
        )}
      </div>

      {/* Контролы: те же круглые кнопки, что на экране звонка, но
          компактнее. Логика та же (call.toggleMic/toggleCamera/leave) —
          изменён только размер и обёртка. */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: space.md,
          padding: `${space.sm}px 0`,
        }}
      >
        <MiniControlButton
          testId="call-mini-toggle-mic"
          title={call.micOn ? t("ui.vyklyuchit_mikrofon") : t("ui.vklyuchit_mikrofon")}
          on={call.micOn}
          onClick={call.toggleMic}
        >
          {call.micOn ? <IconMic size={16} /> : <IconMicOff size={16} />}
        </MiniControlButton>
        <MiniControlButton
          testId="call-mini-toggle-camera"
          title={call.cameraOn ? t("ui.vyklyuchit_kameru") : t("ui.vklyuchit_kameru")}
          on={call.cameraOn}
          onClick={call.toggleCamera}
        >
          {call.cameraOn ? <IconVideo size={16} /> : <IconVideoOff size={16} />}
        </MiniControlButton>
        <MiniControlButton
          testId="call-mini-leave"
          title={
            call.soloSecondsLeft !== null && call.soloSecondsLeft !== undefined
              ? t("ui.otmenit_zvonok")
              : t("room.callLeave")
          }
          on={true}
          danger
          onClick={call.leave}
        >
          <IconPhoneOff size={16} />
        </MiniControlButton>
      </div>
      <CallDiagnosticsButtonMini onCopy={call.copyDiagnostics} />
    </div>
  );
}

/** Диагностика остаётся доступна и в свёрнутом активном звонке. */
function CallDiagnosticsButtonMini({ onCopy }: { onCopy: () => Promise<void> }) {
  const t = useT();
  const [feedback, setFeedback] = useState<"ok" | "error" | null>(null);
  const timerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);
  const copy = () => {
    void onCopy().then(
      () => setFeedback("ok"),
      () => setFeedback("error"),
    ).finally(() => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => setFeedback(null), 2500);
    });
  };
  return (
    <div style={{ padding: `0 ${space.sm}px ${space.sm}px`, textAlign: "center" }}>
      <button
        type="button"
        data-testid="call-mini-copy-diagnostics"
        onClick={copy}
        style={{
          border: "1px solid rgba(255,255,255,0.3)",
          borderRadius: radius.sm,
          background: "rgba(255,255,255,0.1)",
          color: "#fff",
          padding: "5px 8px",
          font: "inherit",
          fontSize: 11,
          cursor: "pointer",
        }}
      >{t("call.copyDiagnostics")}</button>
      {feedback && <div role="status" style={{ marginTop: 3, fontSize: 10, color: feedback === "ok" ? "#fff" : "#ffb3b3" }}>
        {feedback === "ok" ? t("call.diagnosticsCopied") : t("call.diagnosticsCopyFailed")}
      </div>}
    </div>
  );
}

/** Круглая кнопка мини-фрейма: уменьшенная копия CallControlButton из
 *  RoomView (там 48px, здесь 32). Отдельный компонент, а не импорт из
 *  RoomView: экспортировать его внутренние кнопки значило бы связать
 *  файлы, а дублирование 30 строк дешевле. */
function MiniControlButton({
  testId,
  title,
  on,
  danger,
  onClick,
  children,
}: {
  testId: string;
  title: string;
  on: boolean;
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      title={title}
      aria-pressed={danger ? undefined : on}
      onClick={onClick}
      style={{
        width: 32,
        height: 32,
        borderRadius: radius.circle,
        border: "none",
        cursor: "pointer",
        display: "grid",
        placeItems: "center",
        /* Тот же закон, что в полноэкранных контролах (Антон 12.09 10:24):
           приглушённость = «нажать нельзя». Выключенные микрофон/камера
           РАБОТАЮТ, поэтому иконка остаётся полной яркости, а состояние
           показывает перечёркнутая иконка и более тёмный фон. */
        background: danger
          ? color.accent
          : on
            ? "rgba(255,255,255,0.18)"
            : "rgba(255,255,255,0.10)",
        color: danger ? color.textOnAccent : "#fff",
      }}
    >
      {children}
    </button>
  );
}

/**
 * Аватар говорящего без камеры в мини-фрейме (п.6, 2026-09-18).
 *
 * Отдельный компонент, потому что useAuthedMedia — хук: вызвать его внутри
 * условной ветки рендера CallMiniFrame нельзя. Аватар медиа-репозитория
 * требует Bearer, голый <img src> не работает (media.ts).
 */
function MiniSpeakerAvatar({
  client,
  label,
  avatarMxc,
  identity,
}: {
  client: MatrixClient;
  label: string;
  avatarMxc: string | null;
  identity: string;
}) {
  const { url } = useAuthedMedia(client, avatarMxc);
  const initials = label.replace(/^[#!@]/, "").slice(0, 2).toUpperCase();
  return (
    <div
      data-testid="call-mini-speaker-avatar"
      style={{
        width: "100%",
        height: "100%",
        display: "grid",
        placeItems: "center",
        background: colorOf(identity),
        color: color.textOnAccent,
        fontSize: 22,
        fontWeight: 700,
        userSelect: "none",
        overflow: "hidden",
      }}
    >
      {url ? (
        <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        initials
      )}
    </div>
  );
}

/** Видео мини-фрейма: remote играет без звука здесь (звук уже attached
 * отдельно в rtc.ts), local PiP всегда muted; screen использует contain. */
function MiniVideoElement({
  stream,
  muted,
  fit = "cover",
}: {
  stream: MediaStream;
  muted: boolean;
  fit?: "cover" | "contain";
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.srcObject = stream;
    }
    return () => {
      if (ref.current) ref.current.srcObject = null;
    };
  }, [stream]);
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted={muted}
      style={{ width: "100%", height: "100%", objectFit: fit }}
    />
  );
}
